import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EllipsisVertical, Trash, Download, ListPlus, UnfoldHorizontal, RefreshCw, Globe, GlobeLock } from "lucide-react";
import { Leaderboard, MusicalNotes } from '../navbar/SocialIcons';

const CustomDropdown = ({ author, user, userSong, loading, handlePublish, handleRecreate, downloadSong, handleExtend, handleClone, AddToPlaylist, handleAddToLeaderboard, handleOpenDeleteDialog, song, onDropdownOpenChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    // Prevent body scroll when dropdown is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const updatePosition = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const requiredSpace = 200; // Space needed for dropdown
            const spaceBelow = window.innerHeight - rect.bottom;
            // If space below is less than required, show above
            const showAbove = spaceBelow < requiredSpace;
            setPosition({
                // If showing above, position it above the trigger with enough space for the dropdown
                top: showAbove ? rect.top - 250 : rect.bottom,
                left: Math.max(10, rect.left - 200),
                transformOrigin: showAbove ? 'bottom' : 'top'
            });
        }
    };

    const handleClick = (e) => {
        e.stopPropagation();
        if (isOpen) {
            setIsOpen(false);
            onDropdownOpenChange(false);
        } else {
            setIsOpen(true);
            onDropdownOpenChange(true);
        }

    };

    const handleOverlayClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
        onDropdownOpenChange(false);
    };

    useEffect(() => {
        if (isOpen) {
            updatePosition();
            window.addEventListener('resize', updatePosition);
        }
        return () => {
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen]);

    const dropdownContent = isOpen && createPortal(
        <>
            {/* Overlay */}
            <div className="fixed inset-0 bg-transparent z-[99998]" onClick={handleOverlayClick} />
            {/* Dropdown */}
            <div className="fixed z-[99999] text-sm" style={{ top: `${position.transformOrigin === 'top' ? position.top + 5 : position.top + (userSong?.prompt?.type === "create" || userSong?.prompt?.type === "custom" ? 75 : 105)}px`, left: `${position.left + 100}px`, transformOrigin: position.transformOrigin }} onClick={e => e.stopPropagation()}>
                <div className="bg-slate-800/95 dark:bg-gradient-to-br dark:from-pink-200/95 dark:via-pink-300/95 dark:to-pink-400/95 backdrop-blur w-auto border border-slate-950 dark:border-pink-500 text-slate-200 dark:text-black rounded-lg dark:font-semibold p-1">
                    {(author || user?.username === userSong?.username) && (
                        <button disabled={loading} onClick={(e) => { e.stopPropagation(); handlePublish(e); setIsOpen(false); }} className="w-full cursor-pointer p-1.5 focus:text-black text-blue-400 hover:bg-blue-400 hover:text-black flex items-center gap-2 focus:outline-none rounded">
                            {!userSong?.publish ? <Globe /> : <GlobeLock />}
                            <span>{!userSong?.publish ? "Publish" : "Unpublish"}</span>
                        </button>
                    )}
                    {(userSong?.prompt?.type === "create" || userSong?.prompt?.type === "custom") && (
                        <button onClick={(e) => { e.stopPropagation(); handleRecreate(userSong); setIsOpen(false); }} className="w-full cursor-pointer p-1.5 focus:text-black text-violet-400 dark:text-violet-800 hover:bg-violet-400 hover:text-black flex items-center gap-2 focus:outline-none rounded">
                            <RefreshCw size={15} />
                            <span>Recreate</span>
                        </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); downloadSong(song); setIsOpen(false); }} className="w-full cursor-pointer p-1.5 focus:text-black text-cyan-400 dark:text-cyan-600 hover:bg-cyan-400 hover:text-black flex items-center gap-2 focus:outline-none rounded">
                        <Download size={15} />
                        <span>Download</span>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleExtend(e); setIsOpen(false); }} className="w-full cursor-pointer p-1.5 focus:text-black text-green-400 dark:text-green-800 hover:bg-green-400 hover:text-black flex items-center gap-2 focus:outline-none rounded">
                        <UnfoldHorizontal size={15} />
                        <span>Extend</span>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleClone(e); setIsOpen(false); }} className="w-full cursor-pointer p-1.5 focus:text-black text-cyan-400 dark:text-cyan-800 hover:bg-cyan-400 hover:text-black flex items-center gap-2 focus:outline-none rounded">
                        <MusicalNotes size={15} />
                        <span>Clone</span>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); AddToPlaylist(e); setIsOpen(false); }} className="w-full cursor-pointer p-1.5 focus:text-black text-fuchsia-400 dark:text-fuchsia-800 hover:bg-fuchsia-400 hover:text-black flex items-center gap-2 focus:outline-none rounded">
                        <ListPlus size={15} />
                        <span>Add to Playlist</span>
                    </button>
                    {(author || user?.username === userSong?.username) && (
                        <button onClick={(e) => { e.stopPropagation(); handleAddToLeaderboard(e); setIsOpen(false); }} className="w-full cursor-pointer p-1.5 focus:text-black text-emerald-400 dark:text-emerald-800 hover:bg-emerald-400 hover:text-black flex items-center gap-2 focus:outline-none rounded">
                            <Leaderboard size={15} />
                            <span>Add to Leaderboard</span>
                        </button>
                    )}
                    {author && (
                        <button onClick={(e) => { e.stopPropagation(); handleOpenDeleteDialog(e); setIsOpen(false); }} className="w-full cursor-pointer p-1.5 focus:text-black text-red-500 dark:text-red-800 hover:bg-red-500 hover:text-black flex items-center gap-2 focus:outline-none rounded">
                            <Trash size={15} />
                            <span>Delete</span>
                        </button>
                    )}
                </div>
            </div>
        </>,
        document.body
    );

    return (
        <div className="relative">
            <button ref={triggerRef} onClick={handleClick} className="flex justify-end hover:text-green-400 dark:hover:text-pink-500 text-slate-300 dark:text-slate-700">
                <EllipsisVertical size={22} />
            </button>
            {dropdownContent}
        </div>
    );
};

export default CustomDropdown; 